import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  ChevronDown,
  ChevronUp,
  MessageCircle,
  MoreVertical,
  Send,
  Pencil,
  Check,
  X,
} from "lucide-react";
import PhotoGrid from "../../EventManagement/Component/PhotoGrid";
import {
  deleteHubPost,
  formatTimeAgo,
  getInitials,
  isSuperAdmin,
  postComment,
  resolveAuthorNameForPost,
  updateHubPost,
  useHubPostComments,
} from "../../../data/communityHub";
import { useAuth } from "../../../providers/auth";
import { useUser } from "../../../providers/database";
import toast from "react-hot-toast";
import { useQueryClient } from "react-query";
import CommunityCommentItem from "./CommunityCommentItem";
import CommunityPostContent, {
  formatCommentCount,
  getPostEditableText,
} from "./CommunityPostContent";
import CommunityConfirmDialog from "../shared/CommunityConfirmDialog";

const AVATAR_COLORS = [
  "#15415E",
  "#1E596D",
  "#122F46",
  "#183852",
  "#2D7D9A",
  "#0E4D6B",
];

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function FeedAvatar({
  initials,
  name,
  avatarUrl,
  size = 40,
}: {
  initials: string;
  name: string;
  avatarUrl?: string;
  size?: number;
}) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: getAvatarColor(name),
        color: "#fff",
        fontWeight: 700,
        fontSize: size * 0.38,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

export default function CommunityFeedPost({
  hubId,
  post,
  canModerate,
  canComment = false,
  commentEditWindowMs,
  onOpenLightbox,
}: {
  hubId: string;
  post: any;
  canModerate: boolean;
  canComment?: boolean;
  commentEditWindowMs?: number;
  onOpenLightbox: (images: string[], index: number) => void;
}) {
  const { user }: any = useAuth();
  const { data: userProfile }: any = useUser(user?.uid);
  const qc = useQueryClient();
  const [currentAuthorName, setCurrentAuthorName] = useState("");

  useEffect(() => {
    let cancelled = false;
    resolveAuthorNameForPost(user, userProfile).then((name) => {
      if (!cancelled) setCurrentAuthorName(name);
    });
    return () => {
      cancelled = true;
    };
  }, [user, userProfile]);
  const { data: comments }: any = useHubPostComments(hubId, post.id);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(() =>
    getPostEditableText({ title: post.title, body: post.body }),
  );
  const [commentText, setCommentText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const commentTextareaRef = useRef(null) as any;
  const editPostTextareaRef = useRef(null) as any;

  const authorName = post.author?.name || "Unknown";
  const authorInitials = authorName
    .split(" ")
    .map((w: string) => w[0]?.toUpperCase())
    .join("");
  const autoResizeComment = useCallback(() => {
    const el = commentTextareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }
  }, []);

  const autoResizeEditPost = useCallback(() => {
    const el = editPostTextareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 280)}px`;
    }
  }, []);

  useEffect(() => {
    autoResizeComment();
  }, [commentText, autoResizeComment]);

  useEffect(() => {
    if (editing) {
      setEditText(getPostEditableText({ title: post.title, body: post.body }));
      autoResizeEditPost();
    }
  }, [editing, post.title, post.body, autoResizeEditPost]);

  useEffect(() => {
    if (editing) autoResizeEditPost();
  }, [editText, editing, autoResizeEditPost]);

  const handlePostComment = async () => {
    const trimmed = commentText.trim();
    if (!trimmed || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const authorName = await resolveAuthorNameForPost(user, userProfile);
      await postComment(hubId, post.id, {
        text: trimmed,
        author: {
          name: authorName,
          email: user?.email || "",
          photoURL:
            user?.photoURL || user?.picture || userProfile?.photoURL || "",
        },
      });
      qc.invalidateQueries(["hubPostComments", post.id, hubId]);
      setCommentText("");
    } catch {
      toast.error("Failed to post comment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handlePostComment();
    }
  };

  const handleSavePost = async () => {
    const trimmed = editText.trim();
    if (!trimmed) {
      toast.error("Post cannot be empty");
      return;
    }
    try {
      await updateHubPost(hubId, post.id, {
        title: "",
        body: trimmed,
      });
      qc.invalidateQueries(["hubPosts", hubId]);
      setEditing(false);
      toast.success("Post updated");
    } catch {
      toast.error("Failed to update post");
    }
  };

  const handleCancelEdit = () => {
    setEditText(getPostEditableText({ title: post.title, body: post.body }));
    setEditing(false);
  };

  const handleDeletePost = async () => {
    try {
      await deleteHubPost(hubId, post.id);
      qc.invalidateQueries(["hubPosts", hubId]);
      toast.success("Post deleted");
    } catch {
      toast.error("Failed to delete post");
      throw new Error("delete failed");
    }
  };

  const commentCount = comments?.length ?? 0;
  const currentUserEmail = user?.email || "";
  const superAdmin = isSuperAdmin(user?.claims?.role);
  const userInitials = getInitials(currentAuthorName);

  const images = post.images || [];

  const handleImageClick = (index: number) => {
    onOpenLightbox(images, index);
  };

  return (
    <article className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <FeedAvatar
              initials={authorInitials}
              name={authorName}
              avatarUrl={post.author?.avatarUrl}
              size={44}
            />
            <div>
              <span className="font-bold text-[15px] text-[#15415E]">
                {authorName}
              </span>
              <p className="text-xs text-gray-400 mt-0.5">
                {formatTimeAgo(post.createdAt)}
              </p>
            </div>
          </div>
          {canModerate && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
              >
                <MoreVertical size={20} />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-8 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]">
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                    onClick={() => {
                      setMenuOpen(false);
                      setEditText(
                        getPostEditableText({
                          title: post.title,
                          body: post.body,
                        }),
                      );
                      setEditing(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    onClick={() => {
                      setMenuOpen(false);
                      setDeleteConfirmOpen(true);
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {editing ? (
          <div className="space-y-3 mb-4">
            <textarea
              ref={editPostTextareaRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={4}
              className="w-full border border-[#15415E]/30 rounded-lg px-3 py-2.5 text-[15px] text-[#374151] leading-relaxed min-h-[100px] max-h-[280px] resize-y focus:outline-none focus:border-[#15415E] focus:ring-1 focus:ring-[#15415E]/20"
              placeholder="Edit your post..."
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSavePost}
                className="px-4 py-2 text-sm font-semibold bg-instant-teams-teal-Main text-white rounded-lg hover:opacity-90"
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <CommunityPostContent
            title={post.title}
            body={post.body}
            className="mb-4"
          />
        )}

        {!editing && images.length > 1 ? (
          <PhotoGrid images={images} onImageClick={handleImageClick} />
        ) : !editing && images.length === 1 ? (
          <div
            className="relative w-full h-[280px] rounded-xl overflow-hidden cursor-pointer group"
            onClick={() => handleImageClick(0)}
          >
            <img
              src={images[0]}
              alt={post.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
          </div>
        ) : null}
      </div>

      <div className="border-t border-gray-100">
        <button
          type="button"
          onClick={() => setCommentsOpen(!commentsOpen)}
          className="w-full flex items-center justify-between px-6 py-3 text-sm font-semibold text-[#15415E] hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <MessageCircle size={16} />
            <span>{formatCommentCount(commentCount)}</span>
          </div>
          {commentsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {commentsOpen && (
          <div className="px-6 pb-4">
            <div className="space-y-1">
              {comments?.map((comment: any) => (
                <CommunityCommentItem
                  key={comment.id}
                  comment={comment}
                  currentUserEmail={currentUserEmail}
                  hubId={hubId}
                  postId={post.id}
                  isSuperAdminUser={superAdmin}
                  canModerateComments={canModerate}
                  editWindowMs={commentEditWindowMs}
                  showRowHover
                />
              ))}
              {(!comments || comments.length === 0) && (
                <p className="text-sm text-gray-400 text-center py-2">
                  No comments yet. Be the first to comment!
                </p>
              )}
            </div>

            {(canModerate || canComment) && (
              <div className="flex items-start gap-3 mt-4 pt-3 border-t border-gray-100">
                <FeedAvatar
                  initials={userInitials}
                  name={currentAuthorName}
                  avatarUrl={user?.photoURL || user?.picture || userProfile?.photoURL}
                  size={32}
                />
                <div className="flex-1 relative">
                  <textarea
                    ref={commentTextareaRef}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Write a comment... (Enter to post, Shift+Enter for new line)"
                    rows={1}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 pr-10 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#15415E] focus:ring-1 focus:ring-[#15415E]/20 transition-colors resize-none overflow-hidden"
                  />
                  <button
                    type="button"
                    onClick={handlePostComment}
                    disabled={!commentText.trim() || isSubmitting}
                    className={`absolute right-2 bottom-2 p-1.5 rounded-full transition-colors ${
                      commentText.trim() && !isSubmitting
                        ? "text-[#15415E] hover:bg-[#15415E]/10"
                        : "text-gray-300"
                    }`}
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {deleteConfirmOpen && (
        <CommunityConfirmDialog
          title="Delete post?"
          message="This will permanently delete this post and all of its comments. This action cannot be undone."
          confirmLabel="Delete post"
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={handleDeletePost}
        />
      )}
    </article>
  );
}
